import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
} from "@angular/core";
import { MatMenuTrigger } from "@angular/material/menu";
import { FcmService } from "src/app/fcm.service";
import { AuthService } from "src/app/pages/auth/authServices";
import * as moment from "moment";
import { Subscription } from "rxjs";
import { isToday, isYesterday, subDays, isAfter } from 'date-fns';
import { ConfigService } from 'src/app/services/config.service';

@Component({
  selector: "vex-toolbar-notifications",
  templateUrl: "./toolbar-notifications.component.html",
  styleUrls: ["./toolbar-notifications.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ToolbarNotificationsComponent implements OnInit, OnDestroy {
  @ViewChild(MatMenuTrigger) menuTrigger: MatMenuTrigger;
  dropdownOpen: boolean;
  notifications: any[] = [];
  unreadCount: number = 0;
  filterType = "all"; // Default filter type is 'all'
  // filteredNotifications: any[] = [];
  filteredNotifications: {
  today: any[];
  yesterday: any[];
  last7Days: any[];
  older: any[];
} = { today: [], yesterday: [], last7Days: [], older: [] };

  isLoading: boolean = true; // Track loading state
  private notificationBuffer: any[] = [];
  private notificationTimeout: any;
  userIsAuthenticated = false;
  userId: string;
  private authStatusSub: Subscription;
  private visibilityChangeListener: any;
  roleName: string;
  constructor(
    private cd: ChangeDetectorRef,
    private fcmService: FcmService,
    private service: AuthService,
    private configService: ConfigService
  ) {}

  getTimeAgo(timestamp: string): string {
    const now = moment();
    const time = moment(timestamp);
    const diffInMinutes = now.diff(time, "minutes");
    const diffInHours = now.diff(time, "hours");
    const diffInDays = now.diff(time, "days");

    if (diffInMinutes < 1) {
      return "Now";
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes} min ago`;
    } else if (diffInHours < 24) {
      return `${diffInHours} hr ago`;
    } else if (diffInDays < 7) {
      return `${diffInDays} days ago`;
    } else {
      return time.format("D MMM");
    }
  }

  ngOnInit() {
    if (this.configService.areNotificationsDisabled()) {
      this.isLoading = false;
      return;
    }

    this.setupAuthentication();
    this.loadNotifications();
    this.roleName = this.service.getRoleName();
    
    // Listen for visibility changes (when tab becomes active)
    this.setupVisibilityChangeListener();
    
    this.fcmService.currentMessage.subscribe((message) => {
      if (message && message.data?.roleName){
        this.notificationBuffer.push({
          title: message.notification?.title || "Notification",
          caseNumber: message.data?.caseNumber || "",
          link: (message.data?.link && message.data.link.trim() !== '') ? message.data.link : null,
          id: message.data?.id || "",
          message: message.notification?.body || "No content available",
          unread: (message.data?.unread ?? 'true') === 'true',
          roleName: message.data?.roleName || "",
          createdAt: new Date().toISOString(),
        });

        this.updateUnreadCountFromServer();

        if (!this.notificationTimeout) {
          this.notificationTimeout = setTimeout(() => {
            this.processBufferedNotifications();
          }, 500);
        }
      }
    });

     this.filteredNotifications = this.groupNotificationsByDate(this.notifications);
  }

  private setupAuthentication() {
    this.userIsAuthenticated = this.service.getIsAuth();
    this.authStatusSub = this.service
      .getAuthStatusListener()
      .subscribe((isAuthenticated) => {
        this.userIsAuthenticated = isAuthenticated;
        this.userId = this.service.getUserId();
        
      });
  }

  private processBufferedNotifications() {
    this.notifications = [...this.notificationBuffer, ...this.notifications];
    // Update unread count from server to ensure accuracy
    this.updateUnreadCountFromServer();
    this.applyFilter();
    this.cd.markForCheck();

    // Clear the buffer and timeout
    this.notificationBuffer = [];
    this.notificationTimeout = null;
  }

  loadNotifications() {
    this.service.getNotifications().subscribe(
      (data: any[]) => {
        // Process notifications to handle null/empty links
        this.notifications = data.map(notification => ({
          ...notification,
          link: (notification.link && notification.link.trim() !== '') ? notification.link : null,
          caseNumber: notification.caseNumber || ""
        }));
        this.updateUnreadCountFromServer();
        this.applyFilter();
        this.isLoading = false; 
        this.cd.markForCheck();
      },
      (error) => {
        console.error("Error loading notifications:", error);
        this.isLoading = false; 
      }
    );
  }

  updateUnreadCount() {
    this.unreadCount = this.notifications.filter(
      (notification) => notification.unread
    ).length;
  }

  markNotificationAsRead(notification: any) {
    if (notification.unread) {
      this.service.markAsRead(notification._id).subscribe(
        () => {
          notification.unread = false;
          // Update unread count immediately
          this.updateUnreadCountFromServer();
          this.applyFilter();
          this.cd.markForCheck();
        },
        (error) => console.error("Error marking notification as read:", error)
      );
    }
  }

groupNotificationsByDate(notifications: any[]) {
  const grouped = {
    today: [],
    yesterday: [],
    last7Days: [],
    older: []
  };

  const now = moment();

  for (const notification of notifications) {
    const createdAt = moment(notification.createdAt);
    const diffDays = now.diff(createdAt, 'days');

    if (diffDays === 0) {
      grouped.today.push(notification);
    } else if (diffDays === 1) {
      grouped.yesterday.push(notification);
    } else if (diffDays <= 7) {
      grouped.last7Days.push(notification);
    } else {
      grouped.older.push(notification);
    }
  }

  return grouped;
}

getGroupLabel(group: string): string {
  switch (group) {
    case 'today': return 'Today';
    case 'yesterday': return 'Yesterday';
    case 'last7Days': return 'Last 7 Days';
    case 'older': return 'Older';
    default: return '';
  }
}

  hasNotifications(grouped: { [key: string]: any[] }): boolean {
  return Object.values(grouped).some(group => group.length > 0);
}

  onNotificationClick(notification: any, event: Event) {
    event.stopPropagation();
    // Mark notification as read when clicked
    if (notification.unread) {
      this.markNotificationAsRead(notification);
    }
  }

  onCaseNumberClick(notification: any, event: Event) {
    event.stopPropagation();
    // Mark notification as read when case number is clicked
    if (notification.unread) {
      this.markNotificationAsRead(notification);
    }
    this.closeMenu();
  }

  // setFilter(type: string, event: Event): void {
  //   event.stopPropagation();
  //   this.filterType = type;

  //   if (type === "all") {
  //     this.filteredNotifications = this.notifications;
  //   } else if (type === "unread") {
  //     this.filteredNotifications = this.notifications.filter((n) => n.unread);
  //   }
  // }

  setFilter(type: string, event: Event): void {
  event.stopPropagation();
  this.filterType = type;

  let filtered: any[] = [];

  if (type === "all") {
    filtered = this.notifications;
  } else if (type === "unread") {
    filtered = this.notifications.filter((n) => n.unread);
  }

  this.filteredNotifications = this.groupNotificationsByDate(filtered);
}


  closeMenu() {
    this.menuTrigger.closeMenu();
  }

  markAllAsRead(event: Event) {
    event.stopPropagation();
    this.service.markAllAsRead().subscribe(
      () => {
        this.notifications.forEach(
          (notification) => (notification.unread = false)
        );
        // this.updateUnreadCount();
        this.isLoading = true; // Indicate loading state
        this.loadNotifications();
      },
      (error) =>
        console.error("Error marking all notifications as read:", error)
    );
  }


  private updateUnreadCountFromServer() {
    this.service.getUnreadCount().subscribe(
      (response) => {
        if (response && response.success && response.unreadCount !== undefined) {
          this.unreadCount = response.unreadCount;
          this.cd.markForCheck(); // Trigger change detection
        }
      },
      (error) => {
        console.error("Error fetching unread count:", error);
      }
    );
  }

  private setupVisibilityChangeListener() {
    this.visibilityChangeListener = () => {
      if (!document.hidden && this.userIsAuthenticated) {
        this.updateUnreadCountFromServer();
        this.loadNotifications();
      }
    };
    document.addEventListener("visibilitychange", this.visibilityChangeListener);
  }

  private cleanupSubscriptions() {
    if (this.authStatusSub) {
      this.authStatusSub.unsubscribe();
    }
    if (this.visibilityChangeListener) {
      document.removeEventListener("visibilitychange", this.visibilityChangeListener);
    }
    if (this.notificationTimeout) {
      clearTimeout(this.notificationTimeout);
    }
    this.notifications = [];
  }

  ngOnDestroy() {
    this.cleanupSubscriptions();
  }

  markAllDelete(event: Event) {
    event.stopPropagation();
    this.service.markAllAsDelete().subscribe(
      () => {
        this.notifications.forEach(
          (notification) => (notification.unread = false)
        );
        this.isLoading = true; // Indicate loading state
        this.loadNotifications();
      },
      (error) =>
        console.error("Error marking all notifications as read:", error)
    );
  }

  onOpenMenu(event: MouseEvent): void {
    event.stopPropagation();
    this.isLoading = true;
    this.updateUnreadCountFromServer();
    this.loadNotifications();
    this.menuTrigger.openMenu();
  }

  // applyFilter() {
  //   if (this.filterType === "all") {
  //     this.filteredNotifications = this.notifications;
  //   } else if (this.filterType === "unread") {
  //     this.filteredNotifications = this.notifications.filter(
  //       (notification) => notification.unread
  //     );
  //   }
  // }

  applyFilter() {
  let filtered: any[] = [];

  if (this.filterType === "all") {
    filtered = this.notifications;
  } else if (this.filterType === "unread") {
    filtered = this.notifications.filter((n) => n.unread);
  }

  this.filteredNotifications = this.groupNotificationsByDate(filtered);
}

}
